import { Component, computed, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
import {
  GeotechnicalAnalysisService,
  IAlternative,
  IAlternativeValue,
  IGeotechnicalValueEngineering,
  IStabilizationMethodCell,
  VALUE_ENGINEERING_STATUS,
} from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DataTableComponent } from 'src/app/@shared/components/data-table/data-table.component';
import { TableColumn, TableConfig } from 'src/app/@shared/models/table.models';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { DialogModule } from "primeng/dialog";
import { OnlyOfficeDocEditorComponent } from "src/app/@shared/components/only-office-doc-editor/only-office-doc-editor.component";
import { ONLY_OFFICE_EDITOR_TYPE } from 'src/app/@core/models/enums';
import { IAttachment, IUser } from 'src/app/@core/models/model';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { JWTService } from 'src/app/contract-management/services/jwt.service';
import { environment } from 'src/environments/environment.dev';
// import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { GEOTECHNICAL_ANALYSIS_SVG } from 'src/app/common/svgs';
import { GenerationLoaderComponent } from 'src/app/@shared/components/generation-loader/generation-loader.component';

@Component({
  selector: 'app-value-engineering-tab',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent, DataTableComponent, DialogComponent, DialogModule, OnlyOfficeDocEditorComponent, GenerationLoaderComponent],
  templateUrl: './value-engineering-tab.component.html',
  styleUrl: './value-engineering-tab.component.scss',
})
export class ValueEngineeringTabComponent implements OnInit, OnDestroy {


  constructor(private toastService: ToastService) {
    this.authService.currentUser.subscribe((user) => {
      this.currentUser = user ?? null;
    });
  }

  private authService = inject(AuthenticationService);

  VALUE_ENGINEERING_STATUS = VALUE_ENGINEERING_STATUS;
  GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
  generationAnimationData: unknown;

  expandedTable: 'stabilizationMethods' | 'alternativeResults' | null = null;

  @ViewChild('methodCellTpl') methodCellTpl!: TemplateRef<any>;
  @ViewChild('valueCellTpl') valueCellTpl!: TemplateRef<any>;

  analysisExcelDialogVisible: boolean = false

  private geoReportDataService = inject(GeoTechnicalAnalysisReportDataService);
  private geotechnicalService = inject(GeotechnicalAnalysisService);
  private socketService = inject(SocketService);
  private socketSubscription?: Subscription;
  // private authService = inject(AuthenticationService);
  private jwtService = new JWTService();

  data = signal<IGeotechnicalValueEngineering | null>(null);
  loading = signal(false);
  error = signal('');

  readonly tableConfig: TableConfig = { paginator: false, emptyMessage: 'No data' };

  status = computed(() => this.data()?.status ?? VALUE_ENGINEERING_STATUS.NOT_STARTED);
  isProcessing = computed(() => this.loading() || this.status() === VALUE_ENGINEERING_STATUS.NOT_STARTED);
  isFailed = computed(() => !this.loading() && this.status() === VALUE_ENGINEERING_STATUS.FAILED);
  isEmpty = computed(
    () =>
      !this.loading() &&
      !this.isFailed() &&
      (this.status() === VALUE_ENGINEERING_STATUS.NOT_STARTED ||
        (!this.stabilizationColumns().length && !this.alternatives().length))
  );

  stabilizationRows = computed(() => this.data()?.stabilizationMethods ?? []);
  alternatives = computed(() => this.data()?.alternatives ?? []);
  private tableAlternatives = computed(() => this.alternatives().filter(a => !a.isCustomAlternative));

  stabilizationColumns = computed<string[]>(() => {
    const rows = this.stabilizationRows();
    if (!rows.length || !rows[0]?.length) return [];
    return rows[0].map((cell: IStabilizationMethodCell) => cell.key);
  });

  stabilizationTableData = computed<Record<string, string | null>[]>(() =>
    this.stabilizationRows().map((row) => {
      const obj: Record<string, string | null> = {};
      row.forEach((cell: IStabilizationMethodCell) => { obj[cell.key] = cell.value; });
      return obj;
    })
  );

  stabilizationTableColumns = computed<TableColumn[]>(() =>
    this.stabilizationColumns().map((key) => ({ field: key, header: this.formatKey(key) }))
  );

  editedAltHeaders = signal<Map<string, string>>(new Map());

  alternativeTableData = computed<Record<string, any>[]>(() => {
    const alts = this.tableAlternatives();
    const stabMethods = this.stabilizationRows();
    if (!alts.length) return [];
    const template = alts[0].values;
    const result: Record<string, any>[] = [];


    const methodToWorkMap = new Map<string, string>();
    for (const row of stabMethods) {
      const work = row.find(c => c.key === 'Stabilization Work' || c.key.startsWith('Stabilization Work '))?.value?.trim();
      const method = row.find(c => c.key === 'Stabilization Method' || c.key.startsWith('Stabilization Method '))?.value?.trim();
      if (work && method) methodToWorkMap.set(method, work);
    }


    type LayerGroup = { layer: string; rows: { templateRow: IAlternativeValue; rowIndex: number }[] };
    const layerGroups: LayerGroup[] = [];
    let currentGroup: LayerGroup | null = null;

    template.forEach((templateRow, rowIndex) => {
      if (templateRow.layer && (!currentGroup || templateRow.layer !== currentGroup.layer)) {
        currentGroup = { layer: templateRow.layer, rows: [] };
        layerGroups.push(currentGroup);
      }
      currentGroup?.rows.push({ templateRow, rowIndex });
    });

    for (const group of layerGroups) {
      // Layer header row
      const headerRow: Record<string, any> = { _isLayerHeader: true, _layer: group.layer };
      alts.forEach((a: IAlternative) => { headerRow[a.key] = null; });
      result.push(headerRow);

      // Existing parameter rows
      for (const { templateRow, rowIndex } of group.rows) {
        const dataRow: Record<string, any> = { _isLayerHeader: false, parameter: templateRow.parameter };
        alts.forEach((a: IAlternative) => {
          dataRow[a.key] = a.values[rowIndex]?.value ?? null;
          dataRow[`${a.key}__status`] = this.status()
        });
        result.push(dataRow);
      }

      // Method rows: one row per unique work, showing the method each alternative applies for that work
      // Use Set<string> per (work, altKey) to deduplicate across multiple parameter rows
      const workToAltMethods = new Map<string, Map<string, Set<string>>>();
      for (const alt of alts) {
        const layerValues = alt.values.filter(v => v.layer === group.layer && v.method?.length > 0);
        for (const val of layerValues) {
          for (const m of val.method) {
            const methodName = m.trim();
            if (!methodName) continue;
            const work = methodToWorkMap.get(methodName);
            if (!work) continue;
            if (!workToAltMethods.has(work)) workToAltMethods.set(work, new Map());
            const altMap = workToAltMethods.get(work)!;
            if (!altMap.has(alt.key)) altMap.set(alt.key, new Set());
            altMap.get(alt.key)!.add(methodName);
          }
        }
      }

      for (const [work, altMap] of workToAltMethods) {
        const methodRow: Record<string, any> = { _isLayerHeader: false, _isMethodRow: true, parameter: work };
        alts.forEach((a: IAlternative) => {
          const methods = altMap.get(a.key);
          methodRow[a.key] = methods?.size ? [...methods].join(', ') : null;
          methodRow[`${a.key}__status`] = VALUE_ENGINEERING_STATUS.COMPLETED;
        });
        result.push(methodRow);
      }
    }

    return result;
  });


  alternativeTableColumns = computed<TableColumn[]>(() => {
    const cols: TableColumn[] = [
      { field: 'parameter', header: 'Properties', type: 'custom', template: 'methodCell' },
    ];
    this.tableAlternatives().forEach((alt: IAlternative) => {
      const customHeader = this.editedAltHeaders().get(alt.key);
      cols.push({
        field: alt.key,
        header: customHeader ?? this.formatKey(alt.key),
        type: 'custom',
        template: 'valueCell',
        editable: true,
        icon: 'pencil',
        iconSource: 'lucide',
      });
    });
    return cols;
  });

  onAltHeaderChange(event: { field: string; header: string }) {
    const map = new Map(this.editedAltHeaders());
    map.set(event.field, event.header);
    this.editedAltHeaders.set(map);

    const analysisId = this.geoReportDataService.analysisReportId();
    if (!analysisId) return;

    this.geotechnicalService.updateAlternativeKey(analysisId, event.field, event.header).subscribe({
      next: (res) => {
        this.toastService.success("Renamed alternative column successfully")
        this.data.set(res.data ?? null);
        this.editedAltHeaders.set(new Map());
      },
      error: () => this.toastService.error('Failed to rename alternative column'),
    });
  }

  get alternativeCustomTemplates() {
    return { methodCell: this.methodCellTpl, valueCell: this.valueCellTpl };
  }

  alternativeRowClass = (row: any): string =>
    row._isLayerHeader ? 'bg-blue-50' : '';

  ngOnInit(): void {
    this.load();
    this.initSocketListeners();
    this.loadGenerationAnimation();
  }

  loadGenerationAnimation(): void {
    fetch('/animations/template_generation.json')
      .then((response) => response.json())
      .then((data) => { this.generationAnimationData = data; })
      .catch((error) => console.error('Error loading animation:', error));
  }

  ngOnDestroy(): void {
    this.socketSubscription?.unsubscribe();
  }

  private initSocketListeners(): void {
    this.socketSubscription = this.socketService
      .on<{ geotechnicalAnalysisId: string; success: boolean; loading?: boolean; error?: string }>(SOCKET_EVENTS.GEOTECHNICAL_VALUE_ENGINEERING)
      .subscribe(([data]) => {
        if (data?.success) {
          this.load();
          if (!data?.loading) {
            this.toastService.success('Value engineering computation completed');
          }
        } else {
          this.toastService.error('Value engineering computation failed');
        }
      });
  }

  load(): void {
    const analysisId = this.geoReportDataService.analysisReportId();
    if (!analysisId) return;

    this.loading.set(true);
    this.error.set('');

    this.geotechnicalService.getValueEngineering(analysisId).subscribe({
      next: (res) => {
        this.data.set(res.data ?? null);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Failed to load value engineering data');
        this.loading.set(false);
      },
    });
  }

  formatKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  analysisExcelPreviewFile = computed<IAttachment | null>(() => {
    const analysisReport = this.geoReportDataService.analysisReport();
    const analysisId = this.geoReportDataService.analysisReportId();
    const analysisFileUrl = analysisReport?.analysisExcelUrl?.trim();
    if (!analysisId || !analysisReport || !analysisFileUrl) return null;
    const analysisName = analysisReport.displayName || analysisReport.name || 'Analysis Excel';
    return {
      _id: analysisId,
      filename: `${analysisName}.xlsx`,
      url: analysisFileUrl,
      fullUrl: analysisFileUrl,
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      isPublic: false,
    };
  });

  activeExcelSheet = signal<string | null>(null);
  currentUser: IUser | null = null;


  analysisExcelEditorConfig = computed<IConfig | null>(() => {
    const previewFile = this.analysisExcelPreviewFile();
    const analysisId = this.geoReportDataService.analysisReportId();
    const canEdit = this.geoReportDataService.permissions()?.canEdit ?? false;
    const sheetName = this.activeExcelSheet();
    if (!previewFile || !analysisId) return null;

    const docUrl = previewFile.fullUrl ?? previewFile.url;
    const versionHash = this.urlVersionHash(docUrl);
    // Include sheet name in key so OnlyOffice reinitializes when target sheet changes
    const sheetSuffix = sheetName ? `-${sheetName.replace(/\s+/g, '_')}` : '';

    const tokenConfig: Partial<IConfig> = {
      document: {
        fileType: 'xlsx',
        key: `${analysisId}-analysis-excel-${versionHash}${sheetSuffix}`,
        title: previewFile.filename,
        url: docUrl,
        permissions: {
          chat: canEdit,
          comment: canEdit,
          download: true,
          edit: canEdit,
          fillForms: canEdit,
          modifyContentControl: canEdit,
          modifyFilter: canEdit,
          print: true,
          protect: canEdit,
          review: canEdit,
          userInfoGroups: [],
        },
      },
      documentType: ONLY_OFFICE_EDITOR_TYPE.EXCEL,
      editorConfig: {
        user: {
          group: analysisId,
          id: this.currentUser?.email ?? '',
          image: this.currentUser?.profilePicture?.url
            ? `${environment.s3Url}/${this.currentUser.profilePicture.url}?timestamp=${new Date().getTime()}`
            : '',
          name: `${this.currentUser?.firstName ?? ''} ${this.currentUser?.lastName ?? ''}`.trim(),
        },
        embedded: { toolbarDocked: 'top' },
        ...(sheetName ? { actionLink: { action: { type: 'sheet', data: sheetName } } } : {}),
        customization: {
          toolbarHideFileName: true,
          hideRightMenu: true,
          compactToolbar: true,
          compactHeader: true,
          comments: canEdit,
          forcesave: true,
          autosave: false,
          uiTheme: 'theme-gray',
          logo: { image: `${environment.s3Url}/inndoc-ai/assets/images/3.png` },
          zoom: -2,
        },
        callbackUrl: `${environment.apiUrl}/public/save-geotechnical-analysis-excel?analysisId=${analysisId}&userId=${this.currentUser?._id ?? ''}`,
      },
      height: '100%',
      width: '100%',
    };

    return {
      ...tokenConfig,
      token: this.jwtService.signToken(tokenConfig, environment.jwtSecret),
    } as IConfig;
  });

  private urlVersionHash(url: string): string {
    const versionMatch = url.match(/[?&]versionId=([^&]+)/);
    if (versionMatch) return versionMatch[1].slice(0, 16);
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      hash = (Math.imul(31, hash) + url.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
  }
}
